import { AuthFailure, AuthUseCase, UNCLASSIFIED_AUTH_FAILURE } from '@pressreader/authentication-types';
import { getErrorResourceKey } from '@pressreader/resources';
import { UNPARSED_ERROR_MESSAGE } from '@pressreader/services';
import { CustomError, HttpError } from '@pressreader/types';

import { AuthStatus, SignInResultDto } from './types';

/** Where in a sign-in or sign-up the failure happened; decides the reason of a failure that is not an HTTP one. */
type AuthFailureStage = 'captcha' | 'request' | 'provider_window' | 'session' | 'profile';

/**
 * The stage `authorizeUser` stands for with this result: a refusing status is the request's verdict, a success
 * leaves only establishing the site session.
 */
function stageOfAuthorization(signInResult: SignInResultDto | undefined): AuthFailureStage {
    return signInResult?.Status === AuthStatus.Success ? 'session' : 'request';
}

const FAILURE_DETAIL_MAX_LENGTH = 100;

/**
 * A failure of the external provider window. The analytics code travels in `reason`, never in `code`: the legacy
 * external sign-in treats an error whose `code` reads "unexpected" as a silent dismissal.
 */
class ExternalAuthError extends CustomError {
    constructor(message: string, public readonly reason: string, public readonly providerCode?: string) {
        super(message);
    }
}

function classifyHttpFailure({ status, message }: HttpError): string {
    if (!status) {
        return 'network_error';
    }
    if (status === 408) {
        return 'timeout';
    }
    if (status === 404 || status >= 500) {
        return 'service_unavailable';
    }
    return !message || message === UNPARSED_ERROR_MESSAGE ? 'unreadable_response' : 'unkeyed_refusal';
}

function classifyFailure(error: unknown, stage: AuthFailureStage): string {
    const resourceKey = getErrorResourceKey(error);
    if (resourceKey) {
        return resourceKey;
    }
    if (error instanceof ExternalAuthError) {
        return error.reason;
    }
    if (error instanceof HttpError) {
        return classifyHttpFailure(error);
    }
    if (stage === 'captcha') {
        return 'captcha_unavailable';
    }
    if (stage === 'session') {
        return 'session_not_established';
    }
    return UNCLASSIFIED_AUTH_FAILURE;
}

/** Masks email addresses and runs of 4+ digits (card numbers); fits the GA4 100-character value limit. */
function maskFailureDetail(text: string): string {
    return text
        .replace(/[^\s@|]{1,64}@[^\s@|]{1,255}/g, '[email]')
        .replace(/\d{4,}/g, '[n]')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, FAILURE_DETAIL_MAX_LENGTH);
}

/**
 * `<stage> | HTTP <status> | <text>`. Text only for a failure without a resource key (with one it is just the key's
 * translation); a provider window failure gives the provider's code, as its text can name the accounts involved.
 */
function describeFailureDetail(error: unknown, stage: AuthFailureStage): string {
    const parts: string[] = [stage];

    if (error instanceof HttpError && error.status) {
        parts.push(`HTTP ${error.status}`);
    }

    if (error instanceof ExternalAuthError) {
        if (error.providerCode) {
            parts.push(`provider ${error.providerCode}`);
        }
    } else if (!getErrorResourceKey(error) && error instanceof Error && error.message) {
        parts.push(error.message);
    }

    return maskFailureDetail(parts.join(' | '));
}

function describeAuthFailure(error: unknown, stage: AuthFailureStage, useCase: AuthUseCase): AuthFailure {
    return {
        reason: classifyFailure(error, stage),
        detail: describeFailureDetail(error, stage),
        useCase,
    };
}

export type { AuthFailureStage };
export { describeAuthFailure, ExternalAuthError, stageOfAuthorization };
